import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config/environment';
import type {
  LoginResponse,
  User,
  UserAnalytics,
  AdminRideListParams,
  AdminRideListResponse,
  AdminRideDetail,
  ActivitySummary,
  DashboardOverviewData,
  Suggestion,
  Report,
  ModerationStatus,
  ReportsBreakdown,
  ReportedUser,
  AdminUserSearchResponse,
  PoolHealth,
  PoolStats,
  BottleneckAnalysis,
  RateLimitStats,
  UserRateLimits,
  RateLimitConfig,
  Festival,
  FestivalCreatePayload,
  FestivalUpdatePayload,
  FestivalStatusUpdatePayload,
} from '../types';

// Extend AxiosRequestConfig to include _retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Token management
const getAuthToken = (): string | null => localStorage.getItem(config.STORAGE_KEYS.AUTH_TOKEN);
const getRefreshToken = (): string | null => localStorage.getItem(config.STORAGE_KEYS.REFRESH_TOKEN);
const setAuthToken = (token: string): void => localStorage.setItem(config.STORAGE_KEYS.AUTH_TOKEN, token);
const setRefreshToken = (token: string): void => localStorage.setItem(config.STORAGE_KEYS.REFRESH_TOKEN, token);
const clearTokens = (): void => {
  localStorage.removeItem(config.STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(config.STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(config.STORAGE_KEYS.USER_INFO);
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post<{ access_token: string; refresh_token?: string }>(
            `${config.API_BASE_URL}${config.ENDPOINTS.REFRESH}`,
            { refresh_token: refreshToken }
          );

          const { access_token, refresh_token: new_refresh_token } = response.data;
          setAuthToken(access_token);
          if (new_refresh_token) setRefreshToken(new_refresh_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and let app handle redirect
          clearTokens();
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clear tokens and let app handle redirect
        clearTokens();
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>(config.ENDPOINTS.LOGIN, { email, password });
    const { access_token, refresh_token, user } = response.data;

    // Check if user is an admin
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      throw new Error('Нямате администраторски права за достъп до този панел.');
    }

    setAuthToken(access_token);
    if (refresh_token) setRefreshToken(refresh_token);
    localStorage.setItem(config.STORAGE_KEYS.USER_INFO, JSON.stringify(user));

    return response.data;
  },

  logout: (): void => {
    clearTokens();
  },

  verifyToken: async (): Promise<User> => {
    // Make a simple request to verify the token is still valid
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  getCurrentUser: (): User | null => {
    const userInfo = localStorage.getItem(config.STORAGE_KEYS.USER_INFO);
    return userInfo ? JSON.parse(userInfo) : null;
  },

  isAuthenticated: (): boolean => {
    return !!getAuthToken();
  }
};

// Analytics API
export const analyticsAPI = {
  // Dashboard overview with all metrics
  getDashboard: () => api.get<DashboardOverviewData>(config.ENDPOINTS.ANALYTICS_OVERVIEW),
  getActivity: (days: number = 30) => api.get<ActivitySummary>(`/api/v1/analytics/activity?days=${days}`),
};

// Suggestions API
export const suggestionsAPI = {
  // Get all pending suggestions for admin review
  getPendingSuggestions: (limit: number = 50, offset: number = 0) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return api.get<Suggestion[]>(`/api/v1/suggestions/admin/pending?${params.toString()}`);
  },

  // Get all suggestions (any status)
  getAllSuggestions: (limit: number = 50, offset: number = 0) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return api.get<Suggestion[]>(`/api/v1/suggestions/admin/all?${params.toString()}`);
  },

  // Approve a suggestion
  approveSuggestion: (id: string, adminNotes: string = '') => {
    return api.post(`/api/v1/suggestions/${id}/approve`, {
      admin_notes: adminNotes
    });
  },

  // Reject a suggestion
  rejectSuggestion: (id: string, adminNotes: string = '') => {
    return api.post(`/api/v1/suggestions/${id}/reject`, {
      admin_notes: adminNotes
    });
  },

  // Delete a suggestion (admin only)
  deleteSuggestion: (id: string) => {
    return api.delete(`/api/v1/suggestions/admin/${id}`);
  }
};

// Reports API
export const reportsAPI = {
  // Get all reported users for admin review
  getReportedUsers: (statusFilter: string | null = null, limit: number = 50, offset: number = 0) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    if (statusFilter) {
      params.append('status_filter', statusFilter);
    }
    return api.get<Report[]>(`/api/v1/admin/reports?${params.toString()}`);
  },

  // Get detailed information about a specific report
  getReportDetails: (reportId: string) => {
    return api.get<Report>(`/api/v1/admin/reports/${reportId}`);
  },

  // Resolve a report with admin notes
  resolveReport: (reportId: string, adminNotes: string = '') => {
    return api.post(`/api/v1/admin/reports/${reportId}/resolve`, {
      admin_notes: adminNotes
    });
  },

  // Dismiss a report with admin notes
  dismissReport: (reportId: string, adminNotes: string = '') => {
    return api.post(`/api/v1/admin/reports/${reportId}/dismiss`, {
      admin_notes: adminNotes
    });
  },

  // Get most reported users
  getMostReportedUsers: (limit: number = 50) => {
    return api.get<{ users: ReportedUser[] }>(`/api/v1/admin/reports/most-reported-users?limit=${limit}`);
  },

  // Get user reports breakdown
  getUserReportsBreakdown: (userId: string) => {
    return api.get<ReportsBreakdown>(`/api/v1/admin/users/${userId}/reports-breakdown`);
  }
};

// User Moderation API
export const userModerationAPI = {
  // Ban a user permanently
  banUser: (userId: string, reason: string) => {
    return api.post(`/api/v1/admin/users/${userId}/ban`, {
      reason: reason
    });
  },

  // Remove ban from a user
  unbanUser: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/ban`, {
      data: { reason: reason }
    });
  },

  // Suspend a user temporarily
  suspendUser: (userId: string, reason: string, durationHours: number = 72) => {
    return api.post(`/api/v1/admin/users/${userId}/suspend?duration_hours=${durationHours}`, {
      reason: reason
    });
  },

  // Remove suspension from a user
  removeSuspension: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/suspend`, {
      data: { reason: reason }
    });
  },

  // Issue a warning to a user
  warnUser: (userId: string, reason: string) => {
    return api.post(`/api/v1/admin/users/${userId}/warn`, {
      reason: reason
    });
  },

  // Reset warnings for a user
  resetWarnings: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/warnings`, {
      data: { reason: reason }
    });
  },

  // Get user moderation status
  getUserModerationStatus: (userId: string) => {
    return api.get<ModerationStatus>(`/api/v1/admin/users/${userId}/moderation-status`);
  }
};

// Database Monitoring API
export const databaseMonitoringAPI = {
  // Get database pool health
  getPoolHealth: () => api.get<PoolHealth>('/api/v1/admin/database/pool/health'),

  // Get database pool stats
  getPoolStats: () => api.get<PoolStats>('/api/v1/admin/database/pool/stats'),

  // Test database connections
  testConnections: () => api.get('/api/v1/admin/database/connections/test'),

  // Get database configuration
  getConfig: () => api.get('/api/v1/admin/database/config'),

  // Get bottleneck analysis
  getBottleneckAnalysis: () => api.get<BottleneckAnalysis>('/api/v1/admin/database/system/bottleneck-analysis'),

  // Get load test metrics
  getLoadTestMetrics: () => api.get('/api/v1/admin/database/load-test/metrics'),

  // Clear blacklist cache
  clearBlacklistCache: () => api.post('/api/v1/admin/database/blacklist-cache/clear')
};

// Rate Limit Monitoring API
export const rateLimitMonitoringAPI = {
  // Get global rate limit stats
  getStats: () => api.get<RateLimitStats>('/api/v1/admin/rate-limits/stats'),

  // Get user rate limit info
  getUserRateLimits: (userId: string) => api.get<UserRateLimits>(`/api/v1/admin/rate-limits/user/${userId}`),

  // Reset user rate limits
  resetUserRateLimits: (userId: string) => api.post(`/api/v1/admin/rate-limits/user/${userId}/reset`),

  // Get current user rate limits
  getCurrentUserRateLimits: () => api.get<UserRateLimits>('/api/v1/admin/rate-limits/current-user'),

  // Get rate limit configuration
  getConfig: () => api.get<RateLimitConfig>('/api/v1/admin/rate-limits/config'),

  // Health check
  getHealth: () => api.get('/api/v1/admin/rate-limits/health')
};

// Users Management API (Admin)
export const usersManagementAPI = {
  // The user counts at the top of the page
  getUserAnalytics: () => api.get<UserAnalytics>(config.ENDPOINTS.ANALYTICS_USERS),

  // Get most reported users
  getMostReportedUsers: (limit: number = 50) => {
    return api.get<{ users: ReportedUser[] }>(`/api/v1/admin/reports/most-reported-users?limit=${limit}`);
  },

  // Find any user by name, email or phone number
  searchUsers: (q: string, page: number = 1, size: number = 25) =>
    api.get<AdminUserSearchResponse>('/api/v1/admin/users', { params: { q, page, size } }),

  // Get user moderation status
  getUserModerationStatus: (userId: string) => {
    return api.get<ModerationStatus>(`/api/v1/admin/users/${userId}/moderation-status`);
  },

  // Get user reports breakdown
  getUserReportsBreakdown: (userId: string) => {
    return api.get<ReportsBreakdown>(`/api/v1/admin/users/${userId}/reports-breakdown`);
  },

  // Ban user
  banUser: (userId: string, reason: string) => {
    return api.post(`/api/v1/admin/users/${userId}/ban`, { reason });
  },

  // Unban user
  unbanUser: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/ban`, { data: { reason } });
  },

  // Suspend user
  suspendUser: (userId: string, reason: string, durationHours: number = 72) => {
    return api.post(`/api/v1/admin/users/${userId}/suspend?duration_hours=${durationHours}`, { reason });
  },

  // Remove suspension
  removeSuspension: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/suspend`, { data: { reason } });
  },

  // Warn user
  warnUser: (userId: string, reason: string) => {
    return api.post(`/api/v1/admin/users/${userId}/warn`, { reason });
  },

  // Reset warnings
  resetWarnings: (userId: string, reason: string) => {
    return api.delete(`/api/v1/admin/users/${userId}/warnings`, { data: { reason } });
  },
};

// Rides Management API (Admin)
export const ridesManagementAPI = {
  // Every ride a driver published, with the driver and the seat counts.
  // Backend: GET /api/v1/admin/rides (require_admin).
  listRides: (params: AdminRideListParams) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.append(key, String(value));
    });
    return api.get<AdminRideListResponse>(`/api/v1/admin/rides?${query.toString()}`);
  },

  // One ride with the driver's account and every booking.
  // Backend: GET /api/v1/admin/rides/{id} (require_admin).
  getRide: (rideId: string) => api.get<AdminRideDetail>(`/api/v1/admin/rides/${rideId}`),
};

// Festivals API (Admin)
// Backend: /api/v1/admin/festivals — all routes gated by require_admin.
// Verb convention (per spec §2): PUT for full update, POST for state actions. No PATCH.
export const festivalsAPI = {
  // List all festivals (any status). Admin sees DRAFT too.
  listFestivals: (page: number = 1, size: number = 50) => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(size));
    // Backend route is `/admin/festivals` (no trailing slash). Calling with a
    // trailing slash triggers FastAPI's redirect_slashes 307, which drops the
    // Authorization header on the cross-origin redirect and surfaces as a
    // browser "Network error".
    return api.get<Festival[]>(`/api/v1/admin/festivals?${params.toString()}`);
  },

  // Get one festival by id (any status).
  getFestival: (festivalId: string) => {
    return api.get<Festival>(`/api/v1/admin/festivals/${festivalId}`);
  },

  // Create a new festival. Status defaults to DRAFT on the form side.
  createFestival: (payload: FestivalCreatePayload) => {
    return api.post<Festival>('/api/v1/admin/festivals', payload);
  },

  // Full update via PUT (codebase has no PATCH endpoints).
  updateFestival: (festivalId: string, payload: FestivalUpdatePayload) => {
    return api.put<Festival>(`/api/v1/admin/festivals/${festivalId}`, payload);
  },

  // State action: change status only. POST per the /reports/{id}/resolve convention.
  setFestivalStatus: (festivalId: string, payload: FestivalStatusUpdatePayload) => {
    return api.post<Festival>(`/api/v1/admin/festivals/${festivalId}/status`, payload);
  },

  // Upload (or replace) the festival hero banner. Multipart form-data;
  // backend validates content type + 5MB cap and returns the updated festival
  // with an absolute hero_image_url.
  uploadFestivalHero: (festivalId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Festival>(
      `/api/v1/admin/festivals/${festivalId}/hero`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  },
};

// Maintenance Mode API (Admin)
// Backed by Redis on the server side. Toggling here flips the `maintenance.enabled`
// field on the public /api/v1/config endpoint so every client gates on next poll.
export interface MaintenanceState {
  enabled: boolean;
  message_bg: string;
  message_en: string;
}

export const maintenanceAPI = {
  getStatus: () => api.get<MaintenanceState>('/api/v1/admin/maintenance'),
  setStatus: (payload: MaintenanceState) =>
    api.post<MaintenanceState>('/api/v1/admin/maintenance', payload),
};

// Facebook sharing (Admin). The Page autopost switch, the Page post log, the daily
// digest for the groups people paste into by hand, and the group list.
export interface FacebookAutopostState {
  enabled: boolean;
  page_configured: boolean;
  hourly_cap: number;
  posts_last_hour: number;
}

export type PagePostStatus = 'scheduled' | 'posted' | 'failed' | 'skipped' | 'deleted';

export interface PagePost {
  id: string;
  ride_id: string | null;
  title: string | null;
  status: PagePostStatus;
  external_id: string | null;
  error: string | null;
  created_at: string;
  posted_at: string | null;
}

export interface DigestGroup {
  id: string;
  name: string;
  url: string;
  posted: boolean;
  posted_at: string | null;
}

export interface DigestCity {
  city: string;
  ride_count: number;
  text: string;
  search_url: string;
  groups: DigestGroup[];
}

export interface SocialDigest {
  date: string;
  cities: DigestCity[];
}

export interface SocialGroup {
  id: string;
  name: string;
  url: string;
  city: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialGroupPayload {
  name: string;
  url: string;
  city: string | null;
  active: boolean;
  notes: string | null;
}

export const facebookAPI = {
  getAutopost: () => api.get<FacebookAutopostState>('/api/v1/admin/facebook-autopost'),
  setAutopost: (payload: { enabled: boolean }) =>
    api.post<FacebookAutopostState>('/api/v1/admin/facebook-autopost', payload),
  getPagePosts: (limit = 50) =>
    api.get<{ items: PagePost[] }>('/api/v1/admin/social/page-posts', { params: { limit } }),
  getDigest: (date: string) =>
    api.get<SocialDigest>('/api/v1/admin/social/digest', { params: { date } }),
  setPosted: (payload: { date: string; city: string; group_id: string; posted: boolean }) =>
    api.post<{ posted: boolean; posted_at: string | null }>(
      '/api/v1/admin/social/digest/posted',
      payload
    ),
  getGroups: () => api.get<{ items: SocialGroup[] }>('/api/v1/admin/social/groups'),
  createGroup: (payload: SocialGroupPayload) =>
    api.post<SocialGroup>('/api/v1/admin/social/groups', payload),
  updateGroup: (id: string, payload: SocialGroupPayload) =>
    api.put<SocialGroup>(`/api/v1/admin/social/groups/${id}`, payload),
};

// Version Policy API (Admin) — runtime-set min-required-version floors per
// platform. Redis-backed via FeatureFlagService; falls back to backend env
// defaults when never set. Surfaces on the public /api/v1/config endpoint.
export interface VersionPolicyState {
  ios_min_version: string;
  android_min_version: string;
}

export interface VersionPolicyStatus extends VersionPolicyState {
  // 'redis' when admin has set it, 'config' when falling back to env defaults.
  source: 'redis' | 'config';
}

export const versionPolicyAPI = {
  getStatus: () => api.get<VersionPolicyStatus>('/api/v1/admin/version-policy'),
  setStatus: (payload: VersionPolicyState) =>
    api.post<VersionPolicyStatus>('/api/v1/admin/version-policy', payload),
};

// Active ride bottom sheet kill switch (D2). Redis-backed on the server side.
// False flips `feature_flags.active_ride_bottom_sheet_enabled` on the public
// /api/v1/config endpoint, and the apps draw the old active ride view without a map.
export interface ActiveRideBottomSheetState {
  enabled: boolean;
}

export const activeRideBottomSheetAPI = {
  getStatus: () =>
    api.get<ActiveRideBottomSheetState>('/api/v1/admin/active-ride-bottom-sheet'),
  setStatus: (payload: ActiveRideBottomSheetState) =>
    api.post<ActiveRideBottomSheetState>('/api/v1/admin/active-ride-bottom-sheet', payload),
};

// Web accounts kill switch (W4-09). Redis-backed on the server side.
// True flips `feature_flags.web_accounts_enabled` on the public /api/v1/config
// endpoint, and vozyme.eu shows sign-in and the account features.
export interface WebAccountsState {
  enabled: boolean;
}

export const webAccountsAPI = {
  getStatus: () => api.get<WebAccountsState>('/api/v1/admin/web-accounts'),
  setStatus: (payload: WebAccountsState) =>
    api.post<WebAccountsState>('/api/v1/admin/web-accounts', payload),
};

// Ride-anchored chat switch. Redis-backed on the server side.
// True flips `feature_flags.ride_anchored_chat_enabled` on the public
// /api/v1/config endpoint and turns on the per-trip chat rooms.
export interface RideChatState {
  enabled: boolean;
}

export const rideChatAPI = {
  getStatus: () => api.get<RideChatState>('/api/v1/admin/ride-chat'),
  setStatus: (payload: RideChatState) =>
    api.post<RideChatState>('/api/v1/admin/ride-chat', payload),
};

// Error handling utility
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data as { detail?: string; message?: string };
      return data?.detail || data?.message || 'An error occurred';
    } else if (error.request) {
      // Request was made but no response received
      return 'Network error. Please check your connection.';
    }
  }
  // Something else happened
  if (error instanceof Error) {
    return error.message || 'An unexpected error occurred';
  }
  return 'An unexpected error occurred';
};

export default api;
